import type Pathology from "../../pathology/interfaces/Pathology";
import {OrganizationType} from "@/core/interfaces/organization/Organization";
import type {FileManagerFile} from "@/core/interfaces/FileManagerFile";
import {ProgramBookable} from "@/program/interfaces/ProgramBookable";
import {Backing} from "@/backing/interfaces/backing";
import {Term} from "@/customer/interfaces/term";
import {Knowledgeable} from "@/knowledge-box/interfaces";
import {Attachment} from "@/attachment/interfaces/attachment";
import {ConsumerPathwayStep} from "@/pathway/interfaces/Pathway";
import {BaseSupportProgramAddon, SupportProgramAddonSlug} from "@/program/interfaces/addons";

export interface Program {
    /** The program id. */
    id: number;
    /** The programmable type. */
    programmable_type: string;
    /** The programmable id. */
    programmable_id: number;
    /** The program name. */
    name: string;
    /** The program description. */
    description: string;
    /** The program user communication text. */
    user_communication?: string;
    /** The program onboarding communication text. */
    onboarding_communication?: string;
    /** The program teasing content. */
    teasing_content?: string;
    /** The program heading. */
    short_description?: string;
    /** Program teaser. */
    teaser?: string;
    /** Program hook. */
    hook?: string;
    /** Program additional information. */
    additional_information?: string;
    /** The program extension video url. */
    video_url?: string;
    /** The teaser file. */
    file: FileManagerFile;
    /** The teaser logo. */
    logo: FileManagerFile;
    /** The start date. */
    start_at: string;
    /** The end date. */
    end_at: string;
    /** The enabled state. */
    enabled: boolean;
    /** The program policy. */
    policy: string;
    /** The backing ids **/
    backing_ids: [];
    /** The organization */
    organization: {
        id: number;
        entity_id: number;
        logo: string;
        entity_type: OrganizationType;
    };
    /** Number of attachments linked to the program. */
    attachments_count?: number;
    /**
     * The knowledge boxes associated with the program.
     */
    knowledgeables?: Knowledgeable[];
    /** The program bookable object. */
    bookable?: ProgramBookable;
    /**
     * The whole series of acts of a support program, in the catalogue's order.
     * `bookable` above is its last act, kept for the single-appointment flows.
     */
    bookers?: ProgramBookable[] | null;
    /** The program pathway id. */
    pathway_id?: number;
    /** The next_step if pathway exists */
    next_step?: PathwayStep;
    /** The program user feedback. */
    feedbacks?: Feedback[];
    /** The program user inclusions. */
    inclusions?: ProgramInclusion[];
    /** The program attachments. */
    attachments?: Attachment[];
    /** The program backings. */
    backings?: Backing[];
    /** The programmable */
    programmable?: SupportProgram;
    /** The meta data */
    meta?: SupportProgramMeta;
    /** The number of notification sequences linked to the program. */
    notification_sequences_count?: number;
    /** Whether custom product recommendations are allowed (support programs only). */
    allow_custom_product_recommendations?: boolean;
    /** The program polls */
    polls?: Poll[];
    /** The consumer titles of the polls embedded in the program card, keyed by poll id. */
    poll_titles?: Record<number, string> | null;
}

export interface Feedback {
    /** The feedback id. */
    id: number;
    /** The feedback type. */
    type: "delay" | "reject";
    /** The feedback sub item. */
    feedbackable: RejectFeedback | DelayFeedback;
    /** Whether to hide the notification. */
    hide_notification: boolean;
}

export interface RejectFeedback {
    /** The reject reason. */
    reason: string;
    /** The reject date. */
    rejected_at: string;
}

export interface DelayFeedback {
    /** The reject reason. */
    reason: string;
    /** The delay duration in days. */
    days: number;
    /** The reject date. */
    delayed_at: string;
}


export interface ProgramBundleProgram {
    /** The program id. */
    id: number;
    /** The program name. */
    name: string;
    /** The program logo. */
    logo: FileManagerFile;
    /** The number of consumers (patients) in the program */
    consumer_count: number;
    /** The programmable type. */
    programmable_type?: string;
    /** The programmable id. */
    programmable_id?: number;
    categories?: {
        /** The category id. */
        id: number;
        /** The category name. */
        name: string;
    }[];
}

/** The program snapshot interface. */
export type ProgramSnapshot = Program & SupportProgramPatient;

export interface ProgramInclusion {
    /** The program id. */
    id: number;
    /** The program name. */
    consumer_key: string;
    /** The program group. */
    group: string | null;
    /** The program logo. */
    comments: string;
    /** The program description. */
    operator_code: string;
    /** The inclusion date */
    created_at: string;
}

export interface ProgramInclusionGroup {
    /** The type of the group : included or excluded. */
    type: string;
    /** The group slug. */
    slug: string;
    /** The group label. */
    label: string;
    /** The group description. */
    description: string;
}

export interface ProgramBundle {
    /** The program bundle id. */
    id: number;
    /** The program bundle name. */
    name: string;
    /** The program bundle logo. */
    logo: FileManagerFile;
    /** The program bundle description. */
    description: string;
    /** The program bundle programs. */
    programs: (Program & { consumer_count: number })[];
}

export interface SupportProgram {
    id: number;
    program: Program;
    statistics: SupportProgramStatistics;
    meta: SupportProgramMeta;
    /** The support program pathway summary.*/
    pathway: Pathway;
    /** The support program consumer settings. */
    settings: SupportProgramConsumerSettings;
    /** The support program advices. */
    advices: SupportProgramAdvice[];
    /** The support program advices. */
    static_advices: SupportProgramStaticAdviceGroup[];
    /** The support program terms. */
    terms: Term[];
    /** If the support program has a flyer. */
    has_flyer?: boolean;
    /** The addons to display in the consumer program view. */
    addons?: BaseSupportProgramAddon[];
    /** The program kind declared by its engine, null for a program of no particular kind. */
    kind?: SupportProgramKind | null;
}

export interface Pathway {
    /** The pathway id. */
    id: number;
    /** The support program id. */
    support_program_id: number;
    /** The pathway next step. */
    next_step: PathwayStep;
    /** The number of steps. */
    steps_count: number;
    /** The pathway steps. */
    steps: PathwayStep[];
    /** The pathway description. */
    description: string;
    /** The pathway fallback content. */
    fallback_content?: string;
    /** The pathway fallback message. */
    fallback_message?: string;
}

export interface PathwayStep {
    /** The step id. */
    id: number;
    /** The pathway id. */
    pathway_id: number;
    /** The step index. */
    index: number;
    /** The step image file. */
    image: FileManagerFile;
    /** The step type. */
    type: "survey" | "guidance" | "aftercare" | "survey-bundle" | "onboarding" | "assisted-sale";
    /**
     * The step steppable.
     * TODO: Steppable à définir correctement, any utilisé pour fixer une erreur de build du à l'absence la propriété.
     */
    steppable: any;
    /** The step title. */
    title: string;
    /** The step description. */
    description: string;
    /** The step instructions. */
    instructions: string;
    /** The step action button label */
    action: string;
    /** The consumer step */
    consumer_step: ConsumerPathwayStep;
    /** The step lead */
    lead: string;
    /** The repeating flag */
    repeating: 1 | 0;
    /** The resolved fields */
    resolved: {
        /** Resolved lead. */
        lead: string;
        /** Resolved description. */
        description: string;
        /** Resolved action name. */
        action: string;
    };
    /** The step delay in days */
    delay: number;
    /** The step elapsed days. */
    elapsed: number;
    /** The step delay message. */
    delay_message: string;
    /** The step  consent if any. */
    consent?: PathwayStepConsent;
}

export interface PathwayStepConsent {
    id: number;
    pathway_step_id: number;
    description: boolean;
    document: any;
    action: string;
}

export interface ConsumerPathway {
    /** The organization id. */
    organization_id: number;
    /** The consumer key. */
    consumer_key: string;
    /** The pathway id. */
    pathway_id: number;
}

/**
 * @interface SupportProgram
 *
 * @property {number} id - The program id.
 * @property {SupportProgram} support_program - The parent support program.
 * @property {string} counter_notifications - The counter notifications setting.
 * @property {boolean} pathology_information - The pathology information setting.
 * @property {Pathology[]} pathologies - The program pathologies.
 */
export interface SupportProgramSettings {
    id: number;
    support_program: SupportProgram;
    counter_notifications: string;
    pathology_information: boolean;
    pathologies: Pathology[];
    groups: SupportProgramSettingGroup[];
    meta: SupportProgramMeta;
}

export interface SupportProgramSettingGroup {
    /** The group slug. */
    group: string;
    /** The enabled state. */
    enabled: boolean;
}

/**
 * The support program meta data.
 *
 * @property {number} page - The page.
 * @property {number} size - The size.
 * @property {number} total - The total.
 * @property {object} included - The included group labels.
 * @property {object} excluded - The excluded group labels.
 */
export interface SupportProgramMeta {
    /** The requested page number */
    page: number;
    /** The number of items per page */
    size: number;
    /** The total number of items */
    total: number;
    /** When the served consumers were computed, ISO 8601 */
    cached_at?: string | null;
    /** The support program labels */
    labels: {
        [key: string]: string
    }
    /** The included group labels */
    included: {
        [key: string]: string
    },
    /** The excluded group labels */
    excluded: {
        [key: string]: string
    },
    /** The not excluded group labels */
    not_excluded: {
        [key: string]: string
    },
    /** The support program options */
    options: SupportProgramOptions,
    /** The support program pathway progress trackers */
    progress?: string[]
    /** Whether the API response was degraded because of the extension not accessible for the user.*/
    degraded?: boolean;
    /** The optional groups slugs */
    optional_groups?: string[];
}

export interface SupportProgramOptions {
    hideGroups: boolean,
    minimumVisits: number,
    hideExclusionColumn: boolean,
    autoloadSales: boolean;
    notifications: {
        [groupSlug: string]: boolean;
    }
}

/**
 * @interface SupportProgramStatistics
 *
 * @property {string} mode - The statistics display mode.
 * @property {string} danger - Whether to display warnings.
 * @property {SupportProgramCategory} included - The inclusion groups.
 * @property {SupportProgramCategory} excluded - The exclusion groups.
 */
export interface SupportProgramStatistics {
    mode: string;
    danger: string;
    included: SupportProgramCategory;
    excluded: SupportProgramCategory;

    [key: string]: SupportProgramCategory | string | undefined;
}

/**
 * @interface SupportProgramCategory
 *
 * @property {string} label - The general label.
 * @property {string} short - The short label.
 * @property {number} total - The total.
 * @property {number} ratio - The ratio.
 * @property {number} intersection - Part of the patients that are out of the opposite group.
 * @property {any} key - The statistics groups.
 */
export interface SupportProgramCategory {
    label: string;
    short: string;
    total: number;
    ratio: number;
    intersection: number;

    [key: string]: SupportProgramGroup | number | string | undefined;
}

/**
 * @interface SupportProgramGroup
 *
 * @property {string} label - The group label.
 * @property {string} description - The group description.
 * @property {number} count - The group count.
 * @property {any} excluded - The group exclusions count.
 */
export interface SupportProgramGroup {
    label: string;
    description: string;
    count: number;
    excluded: {
        total: number;
        [key: string]: number;
    };
    optional: boolean;
}

export interface SupportProgramPatient {
    /** The consumer key. */
    key: string;
    /** The last visit dates. */
    last_visits: {
        included: string;
        excluded: string;

        [key: string]: string;
    };
    /** The customer id when the consumer is a beneficiary */
    customer_id?: number;

    addons: SupportProgramAddonSlug[];

    /** The name. */
    name: string;
    /** The birthdate. */
    birthdate: string;
    /** The patient phone number. */
    phone: string | null;
    /** The consumer program groups */
    groups: {
        /** The inclusion groups */
        included: SupportProgramPatientGroup[],
        /** The exclusion groups */
        excluded: SupportProgramPatientGroup[]
    };
    /** @property {object} pathway - The consumer pathway if any **/
    pathway: {
        current: {
            repetition: number;
            step: number;
        },
        progress: any[]
    } | null;
    /** The consumer tracker (extra data) */
    tracker?: {
        columns: {
            label: string;
            value?: string | number;
            action?: string;
        }[]
    };
    /** The support program meta. */
    meta: SupportProgramMeta;
    /** is included ? */
    included: boolean;
    /** is excluded ? */
    excluded: boolean;
    /** The completed status. */
    completed: boolean;
    /** The consumer settings. */
    settings: SupportProgramConsumerSettings[];
    /** The consumer feedbacks. */
    feedbacks: Feedback[];
    /** The consumer inclusions. */
    inclusions: ProgramInclusion[];
    /**
     * Product consumer sales.
     * Given by the program_product relation, only loaded for hospital programs.
     */
    sales: {
        product: {
            presentation: number;
        },
        item: {
            name: string;
        },
        datetime: string;
        quantity: number;
    }[] | null;
}

/**
 * Support Program Patient Group
 *
 * @property {string} slug - The group slug.
 * @property {number} visits - The number of consumer visits for that group.
 * @property {string} last_visit - The last consumer visit date.
 * @property {SupportProgramPatientSale[]} sales - The patient sales.
 */
export interface SupportProgramPatientGroup {
    slug: string;
    visits: number;
    last_visit: string;
    sales?: SupportProgramPatientSale[];
}

/**
 * Support program patient sale interface.
 *
 * @property {string} name - The product name.
 * @property {string} cip - The comma separated product cips.
 * @property {string} date - The sale date.
 */
export interface SupportProgramPatientSale {
    name: string;
    date: string;
    cip?: string;
    quantity?: number;
    price?: number;
}


export type SupportProgramMarketStatistic =
    ConsumerMarketStatistic
    | SaleMarketStatistic
    | ConsumerSaleMarketStatistic
    | CoverageMarketStatistic
    | KpiMarketStatistic
    | PieChartMarketStatistic
    | BarChartMarketStatistic
    | LineChartMarketStatistic
    | BinomialPieChartMarketStatistic
    | GrowthStatistics
    | CompactMarketStatistic;

/**
 * SupportProgramMarketStatistic interface.
 *
 * @property {string} [extra] - The extra statistic label.
 * @property {string} [source] - The source statistic label.
 */
export interface BaseSupportProgramMarketStatistic {
    type: SupportProgramMarketStatisticType;
    label: string;
    value: number | string | [] | object;
    extra?: string | null;
    tooltip: string | null;
    options?: Record<string, unknown>;
    available_options?: Record<string, unknown>;
}

export interface ConsumerMarketStatistic extends BaseSupportProgramMarketStatistic {
    type: SupportProgramMarketStatisticType.CONSUMER;
    value: string;
    extra: string | null;
    source: string | null;
}

export interface SaleMarketStatistic extends BaseSupportProgramMarketStatistic {
    type: SupportProgramMarketStatisticType.SALE,
    value: string;
    extra: string | null;
}

export interface ConsumerSaleMarketStatistic extends BaseSupportProgramMarketStatistic {
    type: SupportProgramMarketStatisticType.CONSUMER_SALE,
    value: string;
    extra: string | null;
}

export interface CoverageMarketStatistic extends BaseSupportProgramMarketStatistic {
    type: SupportProgramMarketStatisticType.COVERAGE,
    value: string;
    extra: string | null;
    source: string | null;
    historisation: string | null;
}

export interface KpiMarketStatistic extends BaseSupportProgramMarketStatistic {
    type: SupportProgramMarketStatisticType.KPI,
    value: string;
    extra: string | null;
    color: string | null;
}

export interface PieChartMarketStatistic extends BaseSupportProgramMarketStatistic {
    type: SupportProgramMarketStatisticType.PIE_CHART,
    value: { name: string; count: number }[];
    extra: string | null;
}

export interface BarChartMarketStatistic extends BaseSupportProgramMarketStatistic {
    type: SupportProgramMarketStatisticType.BAR_CHART,
    value: { [key: string]: { label: string, value: number; x: string | number; y: string | number }[] }
    extra: string | null;
    unit: string;
    interval?: HistogramInterval;
    variant?: 'stacked'|null;
    options?: {
        interval?: HistogramInterval;
    };
    available_options?: {
        interval?: HistogramIntervalOption;
    }
}

export interface LineChartMarketStatistic extends BaseSupportProgramMarketStatistic {
    type: SupportProgramMarketStatisticType.LINE_CHART,
    value: { [key: string]: { label: string, value: number; x: string | number; y: string | number }[] };
    extra: string | null;
    unit: string;
    interval?: HistogramInterval;
    options?: {
        interval?: HistogramInterval;
    };
    available_options?: {
        interval?: HistogramIntervalOption;
    }
}

export interface BinomialPieChartMarketStatistic extends BaseSupportProgramMarketStatistic {
    type: SupportProgramMarketStatisticType.BINOMIAL_PIE_CHART,
    value: { name: string; count: number }[];
    options?: {
        interval?: HistogramInterval;
    };
    available_options?: {
        interval?: HistogramIntervalOption;
    }
}

export interface GrowthStatistics extends BaseSupportProgramMarketStatistic {
    type: SupportProgramMarketStatisticType.GROWTH,
    value: { delta: number, current: number, previous: number };
}

export interface CompactMarketStatistic extends BaseSupportProgramMarketStatistic {
    type: SupportProgramMarketStatisticType.COMPACT,
    value: string | number;
    extra: string | null;
    unit: string;
}

export type HistogramInterval = 'day' | 'month' | 'semester';
export type HistogramIntervalOption = Record<HistogramInterval, string>;

/**
 * SupportProgramAdvice interface.
 *
 * @property {string} slug - The advice slug.
 * @property {string} description - The advice description.
 * @property {ProductAdvice[]} advices - The advices.
 *
 * TODO: Add a type to the support program advice, and make different advice interface for each type.
 */
export interface SupportProgramAdvice {
    slug: string;
    description: string;
    advices: ProductAdvice[];
}

export interface SupportProgramStaticAdviceGroup {
    /** The title. */
    title: string;
    /** The advices. */
    advices: SupportProgramStaticAdvice[];
}

export interface SupportProgramStaticAdvice {
    /** The title. */
    title: string;
    /** The description. */
    description: string;
    /** The top image url. */
    image: string;
    products: {
        /** The product presentation id. */
        id: number;
        /** The product name. */
        name: string;
        /** The product code. */
        code: string;
        /** The product image url. */
        image: string;
    }[];
}

/**
 * ProductAdvice interface.
 *
 * @property {string[]} advices - The product advices as string.
 * @property {string} background_color - The background color (RGBA).
 * @property {string} color - The text color (RGBA).
 * @property {string} image - The image url.
 * @property {string} name - The product name.
 * @property {number[]} products - The product CIPs.
 * @property {string|null} purchased_at - The purchase date.
 */
export interface ProductAdvice {
    /** The product advices content. */
    advices: string;
    /** The background color. */
    background_color: string;
    /** The text color. */
    color: string;
    /** The image url. */
    image: string;
    /**
     * The linked product price.
     */
    price: number | null;
    /** The product stock. */
    stock: number | null;
    /** The product advice name. */
    name: string;
    /** The product CIPs. */
    products: number[];
    /** The purchase date. */
    purchased_at: string | null;
    /** The advice sub title. */
    sub_title: string | null;
    /** The advice images. */
    images: string[];
}

/**
 * Support program market statistic type enum.
 */
export enum SupportProgramMarketStatisticType {
    SALE = "sale",
    CONSUMER = "consumer",
    CONSUMER_SALE = "consumer-sale",
    COVERAGE = "coverage",
    KPI = "kpi",
    PIE_CHART = "pie-chart",
    BAR_CHART = "bar-chart",
    BINOMIAL_PIE_CHART = "binomial-pie-chart",
    LINE_CHART = "line-chart",
    GROWTH = "growth",
    COMPACT = "compact",
}

/**
 * Program types enum.
 */
export enum ProgramType {
    SUPPORT = "support",
    DISCOUNT = "discount",
    LOYALTY_CARD = "loyalty-card",
    FEATURE = "feature"
}

/**
 * Support program kinds, adapting generic features such as the MSSanté message.
 */
export enum SupportProgramKind {
    VACCINATION = "vaccination",
    VETERINARY = "veterinary"
}

export interface SupportProgramConsumerSettings {
    /** The consumer settings id. */
    id: number;
    /** Whether the consumer has been manually disabled from the support program. */
    disabled: boolean;
    /** The inclusion options. */
    options: {
        /** Options key for hospital program inclusion settings. */
        inclusion_date?: string
        /** Options key for hospital program inclusion settings. */
        prescriptor?: string;
        [key: string]: number | boolean | string | undefined;
    },
    /** The unserialized options. */
    unserializedOptions: {
        [key: keyof SupportProgramConsumerSettings["options"]]: UnserializedSupportProgramConsumerSettings;
    };

    support_program: SupportProgram;
}

export interface UnserializedSupportProgramConsumerSettings {
    /** The heading to display. */
    heading?: string;
    /** The label to display. */
    label?: string;
    /** The secure file to display. */
    secure_file?: string;
}

export interface Poll {
    /** The poll id. */
    id: number;
    /** The poll name. */
    name: string;
    /** The poll title. */
    title: string;
    /** The poll description. */
    description: string;
    /** The poll feedback once submitted. */
    feedback: string;
    /** The poll enabled status. */
    enabled: boolean;
    /** Whether the poll is displayed inside the program (teaser card) instead of the top of the notification. */
    display_in_program?: boolean;
    /** The poll entries. */
    entries: PollEntry[];
}

export interface PollEntryAction {
    /** The host serving the routes: 'hds' or null for the main API. */
    host: string | null;
    /** The action HTTP method. */
    method: string | null;
    /** The action route; {consumer_path} resolves to the consumer resource path. */
    route: string;
    /** The action payload; customer_id / consumer_key keys are filled with the consumer values. */
    body: Record<string, unknown> | null;
    /** The cancel HTTP method. */
    cancel_method: string | null;
    /** The cancel route; {id} resolves to the action response id. */
    cancel_route: string | null;
    /** The cancel payload. */
    cancel_body: Record<string, unknown> | null;
}

export interface PollEntry {
    /** The poll entry id. */
    id: number;
    /** The poll entry label. */
    label: string;
    /** The entry actions; supersedes the flat action_* / cancel_* fields. */
    actions: PollEntryAction[] | null;
    /** The action host. */
    action_host: string | null;
    /** The action route. */
    action_route: string | null;
    /** The action method. */
    action_method: string | null;
    /** The action payload. */
    action_body: Record<string, unknown> | null;
    /** The cancel method. */
    cancel_method: string | null;
    /** The cancel route. */
    cancel_route: string | null;
    /** The cancel payload. */
    cancel_body: Record<string, unknown> | null;
}

export interface PollAnswer {
    /** The key of the consumer who answered. */
    consumer_key: string;
    /** The poll id. */
    poll_id: number;
    /** The poll entry id */
    poll_entry_id: number;
    /** The answer date. */
    answered_at: string;
}
